import { useEffect, useMemo, useState } from 'react';
import { useGetCatalogs } from '../../hooks/client/useGetCatalogs';
import { createClient, updateClient, getClaveUnicaByClientId } from '../../services/clientService';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { clientsI18n } from './i18n';

const ClientsCreateModal = ({
  DesignSystemComponents,
  show,
  onClose,
  onSave,
  mode = 'create',
  initialData = null,
}) => {
  const { Modal, Input, Dropdown, ComboBox, IconButton } = DesignSystemComponents;
  const { t } = useScopedTranslation(clientsI18n);

  const {
    nationalityOptions,
    entityTypeOptions,
    genderOptions,
    maritalStatusOptions,
    sacOptions,
    loading: catalogsLoading,
    error: catalogsError,
  } = useGetCatalogs();

  const buildInitialForm = (data) => ({
    clientId: data?.clientId ?? data?.id ?? '',
    fullName: data?.fullName ?? data?.name ?? '',
    identifier: data?.identifier ?? '',
    secretKey: data?.secretKey ?? data?.claveUnica ?? '',
    email: data?.email ?? '',
    phone: data?.phone ?? data?.telephone ?? '',
    phone2: data?.phone2 ?? data?.telephone2 ?? '',
    address: data?.address ?? '',
    nationality: data?.nationality ?? data?.idCountry ?? '',
    entityType: data?.entityType ?? data?.idIdentityType ?? '',
    gender: data?.gender ?? data?.idGenre ?? '',
    maritalStatus: data?.maritalStatus ?? data?.idMaritalStatus ?? '',
    branchOffice: data?.branchOffice ?? data?.idBranchOffice ?? '',
    region: data?.region ?? data?.idRegion ?? '',
    sacLawyer: data?.sacLawyer ?? data?.userIdSac ?? data?.newSac ?? '',
    notes: data?.notes ?? data?.observations ?? '',
    updaterId: data?.updaterId ?? '',
  });

  const [form, setForm] = useState(buildInitialForm());

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [claveLoaded, setClaveLoaded] = useState(false);
  const [claveLoading, setClaveLoading] = useState(false);

  const handleLoadClaveUnica = async () => {
    const clientId = initialData?.clientId ?? initialData?.id;
    if (!clientId || claveLoaded) return;
    try {
      setClaveLoading(true);
      const data = await getClaveUnicaByClientId(clientId);
      const value =
        typeof data === 'string'
          ? data
          : (data?.claveUnica ?? data?.value ?? '');
      setForm((prev) => ({ ...prev, secretKey: value }));
      setClaveLoaded(true);
    } catch (error) {
      console.error('Error fetching clave única:', error);
    } finally {
      setClaveLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      setForm(buildInitialForm(initialData));
      setSaveError(null);
      setClaveLoaded(false);
    }
  }, [show, initialData]);

  const setField = (key) => (eOrValue) => {
    const value =
      eOrValue?.target !== undefined ? eOrValue.target.value : eOrValue;
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaveError(null);
  };

  const setDropdownValue = (key) => (opt) => {
    setForm((prev) => ({ ...prev, [key]: opt?.value ?? '' }));
  };

  const emailValid = useMemo(() => {
    if (!form.email) return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  }, [form.email]);

  const phoneValid = useMemo(() => {
    if (!form.phone) return false;
    return form.phone.replace(/\D/g, '').length >= 7;
  }, [form.phone]);

  const isValid = useMemo(() => {
    const secretKeyValid =
      mode === 'edit'
        ? !claveLoaded || form.secretKey.trim().length > 0
        : form.secretKey.trim().length > 0;

    return (
      form.fullName.trim().length > 0 &&
      form.identifier.trim().length > 0 &&
      secretKeyValid &&
      emailValid &&
      phoneValid &&
      !!form.nationality &&
      !!form.entityType &&
      !!form.gender &&
      !!form.maritalStatus &&
      !!form.sacLawyer
    );
  }, [
    form.fullName,
    form.identifier,
    form.secretKey,
    form.nationality,
    form.entityType,
    form.gender,
    form.maritalStatus,
    form.sacLawyer,
    emailValid,
    phoneValid,
    mode,
    claveLoaded,
  ]);

  const handleSave = async () => {
    if (!isValid) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const response =
        mode === 'edit' ? await updateClient(form) : await createClient(form);

      if (response.success) {
        onSave?.(response);
        onClose();
      } else {
        setSaveError(response.message);
      }
    } catch (error) {
      setSaveError(t('clients_modal_save_error'));
    } finally {
      setIsSaving(false);
    }
  };

  const findOption = (opts, value) =>
    opts.find((o) => o.value === value) ?? null;

  return (
    <Modal
      title={mode === 'edit' ? t('clients_modal_title_edit') : t('clients_modal_title_create')}
      show={show}
      onClose={onClose}
      primaryBtnTxt={mode === 'edit' ? t('clients_modal_save_edit') : t('clients_modal_save_create')}
      primaryBtnAction={handleSave}
      disabledPrimaryBtn={!isValid || catalogsLoading || isSaving}
      secondaryBtnTxt={t('clients_modal_cancel')}
      secondaryBtnAction={onClose}
      ctaAligment='right'
      width='720px'
      height='80vh'
    >
      {catalogsError && (
        <div className='alert alert-danger mb-3' role='alert'>
          {catalogsError}
        </div>
      )}

      {saveError && (
        <div className='alert alert-danger mb-3' role='alert'>
          {saveError}
        </div>
      )}

      {isSaving && (
        <div className='alert alert-info mb-3' role='alert'>
          {t('clients_modal_saving')}
        </div>
      )}

      <div className='row'>
        <div className='col-12'>
          <Input
            label={t('clients_modal_fullName_label')}
            required
            placeholder={t('clients_modal_fullName_placeholder')}
            value={form.fullName}
            onChange={setField('fullName')}
            disabled={catalogsLoading || isSaving}
          />
        </div>
        <div className='col-12'>
          <Input
            label={t('clients_modal_identifier_label')}
            required
            placeholder={t('clients_modal_identifier_placeholder')}
            value={form.identifier}
            onChange={setField('identifier')}
            inputMode='text'
            disabled={catalogsLoading || isSaving}
          />
        </div>
        <div className='col-12'>
          {mode === 'edit' && !claveLoaded ? (
            <div>
              <label className='form-label'>
                {t('clients_modal_secretKey_label')}
              </label>
              <div className='d-flex align-items-center gap-2'>
                <span className='caption'>*****</span>
                <IconButton
                  ariaLabel={t('clients_modal_secretKey_load')}
                  icon='eye'
                  iconFill='var(--dark)'
                  onClick={handleLoadClaveUnica}
                  disabled={claveLoading || isSaving}
                  size='md'
                />
                {claveLoading && (
                  <span className='mini font-light'>
                    {t('clients_modal_secretKey_loading')}
                  </span>
                )}
              </div>
            </div>
          ) : (
            <Input
              label={t('clients_modal_secretKey_label')}
              required={mode === 'create'}
              placeholder={t('clients_modal_secretKey_placeholder')}
              value={form.secretKey}
              onChange={setField('secretKey')}
              disabled={catalogsLoading || isSaving}
            />
          )}
        </div>
        <div className='col-12 col-md-6'>
          <Input
            label={t('clients_modal_email_label')}
            required
            placeholder={t('clients_modal_email_placeholder')}
            value={form.email}
            onChange={setField('email')}
            error={form.email && !emailValid ? t('clients_modal_email_invalid') : ''}
            inputMode='email'
            disabled={catalogsLoading || isSaving}
          />
        </div>
        <div className='col-12 col-md-6'>
          <Input
            label={t('clients_modal_phone_label')}
            required
            placeholder={t('clients_modal_phone_placeholder')}
            value={form.phone}
            onChange={setField('phone')}
            error={form.phone && !phoneValid ? t('clients_modal_phone_invalid') : ''}
            inputMode='tel'
            disabled={catalogsLoading || isSaving}
          />
        </div>
        <div className='col-12 col-md-6'>
          <div className='space-like-input'>
            <ComboBox
              options={nationalityOptions}
              value={
                nationalityOptions.find((o) => o.value === form.nationality) ??
                null
              }
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  nationality: opt?.value ?? '',
                }))
              }
              placeholder={t('clients_modal_nationality_placeholder')}
              label={t('clients_modal_nationality_label')}
              required
              isClearable
              disabled={catalogsLoading || isSaving}
            />
          </div>
        </div>
        <div className='col-12 col-md-6'>
          <div className='space-like-input'>
            <Dropdown
              options={entityTypeOptions}
              value={findOption(entityTypeOptions, form.entityType)}
              onChange={setDropdownValue('entityType')}
              placeholder={t('clients_modal_entityType_placeholder')}
              width='100%'
              label={t('clients_modal_entityType_label')}
              required
              disabled={catalogsLoading || isSaving}
            />
          </div>
        </div>
        <div className='col-12 col-md-6'>
          <div className='space-like-input'>
            <Dropdown
              options={genderOptions}
              value={findOption(genderOptions, form.gender)}
              onChange={setDropdownValue('gender')}
              placeholder={t('clients_modal_gender_placeholder')}
              width='100%'
              label={t('clients_modal_gender_label')}
              required
              disabled={catalogsLoading || isSaving}
            />
          </div>
        </div>
        <div className='col-12 col-md-6'>
          <div className='space-like-input'>
            <Dropdown
              options={maritalStatusOptions}
              value={findOption(maritalStatusOptions, form.maritalStatus)}
              onChange={setDropdownValue('maritalStatus')}
              placeholder={t('clients_modal_maritalStatus_placeholder')}
              width='100%'
              label={t('clients_modal_maritalStatus_label')}
              required
              disabled={catalogsLoading || isSaving}
            />
          </div>
        </div>

        <div className='col-12'>
          {ComboBox ? (
            <div className='space-like-input'>
              <ComboBox
                options={sacOptions}
                value={
                  sacOptions.find((o) => o.value === form.sacLawyer) ?? null
                }
                onChange={(opt) =>
                  setForm((p) => ({ ...p, sacLawyer: opt?.value ?? '' }))
                }
                placeholder={t('clients_modal_sacLawyer_placeholder')}
                label={t('clients_modal_sacLawyer_label')}
                required
                noResultText={t('clients_modal_sacLawyer_noResults')}
                isClearable
                disabled={catalogsLoading || isSaving}
              />
            </div>
          ) : (
            <div className='space-like-input'>
              <Dropdown
                options={sacOptions}
                value={findOption(sacOptions, form.sacLawyer)}
                onChange={setDropdownValue('sacLawyer')}
                placeholder={t('clients_modal_sacLawyer_dropdown_placeholder')}
                width='100%'
                label={t('clients_modal_sacLawyer_label')}
                required
                disabled={catalogsLoading || isSaving}
              />
            </div>
          )}
        </div>
        <div className='col-12'>
          <label className='form-label'>{t('clients_modal_notes_label')}</label>
          <textarea
            className='form-control'
            placeholder={t('clients_modal_notes_placeholder')}
            rows={4}
            value={form.notes}
            onChange={setField('notes')}
            disabled={catalogsLoading || isSaving}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ClientsCreateModal;
